import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SUFFIX = "Lanista — Gladiator Manager";
const DEFAULT_DESC =
  "Lanista is a free online gladiator manager game — recruit, train, and battle gladiators in tactical PvP arena combat. Build your ludus and play free.";
const BASE_URL = "https://lanistamanager.com";

// Landing keeps its rich marketing title verbatim so crawlers hitting "/" see it.
const LANDING_TITLE = "Gladiator Manager Game | Lanista — Free Online Gladiator Game";

// Exact-path titles. Dynamic routes are matched by prefix below.
const TITLES = {
  "/login": `Log In or Play Free | ${SUFFIX}`,
  "/about": `About Lanista | Free Online Gladiator Manager Game`,
  "/faq": `FAQ - Frequently Asked Questions | ${SUFFIX}`,
  "/guide": `How to Play - Complete Guide | ${SUFFIX}`,
  "/forgot-password": `Reset Your Password | ${SUFFIX}`,
  "/reset-password": `Reset Your Password | ${SUFFIX}`,
  "/verify-email": `Verify Your Email | ${SUFFIX}`,
  "/roster": `Active Roster | ${SUFFIX}`,
  "/recruit": `Recruit Pool | ${SUFFIX}`,
  "/training": `Training | ${SUFFIX}`,
  "/equipment": `Equipment & Blacksmith | ${SUFFIX}`,
  "/triumvirate": `Ludus Trias | ${SUFFIX}`,
  "/ludus-trias": `Ludus Trias | ${SUFFIX}`,
  "/triumvirate/opponents": `Ludus Trias Opponents | ${SUFFIX}`,
  "/triumvirate/ladder": `Ludus Trias Ladder | ${SUFFIX}`,
  "/ludus-trias/opponents": `Ludus Trias Opponents | ${SUFFIX}`,
  "/ludus-trias/ladder": `Ludus Trias Ladder | ${SUFFIX}`,
  "/ludus-trias/archive": `Ludus Trias Archive | ${SUFFIX}`,
  "/inbox": `Inbox & Friends | ${SUFFIX}`,
  "/challenges": `Challenges | ${SUFFIX}`,
  "/events": `Events & Community | ${SUFFIX}`,
  "/leaderboard": `Arena Rankings | ${SUFFIX}`,
  "/vip": `VIP Membership | ${SUFFIX}`,
  "/news": `News & Updates | ${SUFFIX}`,
  "/alliance": `Alliances | ${SUFFIX}`,
  "/alliance/customize": `Alliance Look | ${SUFFIX}`,
  "/admin": `Admin | ${SUFFIX}`,
  "/admin/wheel-fortuna": `Wheel of Fortuna Admin | ${SUFFIX}`,
  "/customize": `Customize Ludus | ${SUFFIX}`,
  "/chat": `Arena Chat | ${SUFFIX}`,
};

const DESCRIPTIONS = {
  "/login": "Log in to your Lanista account or create a free gladiator manager ludus. No download required — recruit fighters, train in real time, and battle in tactical PvP arena combat.",
  "/about": "Lanista is a free online gladiator management game. Recruit fighters across six classes, train in real-time, forge equipment, capture and breed beasts, and compete in tactical PvP battles.",
  "/faq": "Frequently asked questions about Lanista Manager. Learn about gladiator classes, training, combat, the beast system, alliances, and everything else you need to dominate the arena.",
  "/guide": "Complete beginner's guide to Lanista Manager. Learn how to recruit gladiators, train effectively, forge equipment, capture beasts, and climb the PvP rankings in this free online gladiator game.",
  "/leaderboard": "See the top gladiators and lanistae in the Lanista arena rankings. Climb the global leaderboard by winning ranked PvP battles.",
  "/triumvirate": "Build one live-scored three-gladiator Ludus Trias squad, challenge rivals from a shared 3v3 opponent pool, and climb a single monthly ladder.",
  "/ludus-trias": "Build one live-scored three-gladiator Ludus Trias squad, challenge rivals from a shared 3v3 opponent pool, and climb a single monthly ladder.",
  "/triumvirate/opponents": "Browse the shared Ludus Trias opponent pool and challenge nearby season ratings in Lanista's unified ranked 3v3 mode.",
  "/triumvirate/ladder": "Review the unified Ludus Trias live ladder, previous laurels, and current season standings.",
  "/ludus-trias/opponents": "Browse the shared Ludus Trias opponent pool and challenge nearby season ratings in Lanista's unified ranked 3v3 mode.",
  "/ludus-trias/ladder": "Review the unified Ludus Trias live ladder, previous laurels, and current season standings.",
  "/ludus-trias/archive": "Browse completed Ludus Trias seasons, final unified ladders, and archived winners by month.",
  "/vip": "Become a VIP Lanista: gain +5 gladiator slots, upload custom profile avatars, fight more often, and dominate the arena. The core gladiator game stays free.",
  "/news": "Latest news, battle headlines and updates from the Lanista gladiator arena.",
  "/admin/wheel-fortuna": "Admin controls for Wheel of Fortuna rewards, slice rarity weights, and the free daily spin reset timing.",
};

function resolveTitle(path) {
  if (path === "/") return LANDING_TITLE;
  if (TITLES[path]) return TITLES[path];
  if (path.startsWith("/gladiator/") && path.endsWith("/skills")) return `Skill Tree | ${SUFFIX}`;
  if (path.startsWith("/gladiator/")) return `Gladiator Profile | ${SUFFIX}`;
  if (path.startsWith("/profile/")) return `Lanista Profile | ${SUFFIX}`;
  if (path.startsWith("/alliance/")) return `Alliance | ${SUFFIX}`;
  return LANDING_TITLE;
}

function setMetaDescription(content) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonicalUrl(path) {
  // Remove any existing canonical tag
  let existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.remove();
  }
  
  // Normalize paths - use primary URL for canonical
  let canonicalPath = path;
  if (path === '/privacy-policy') canonicalPath = '/privacy';
  if (path === '/terms-of-service') canonicalPath = '/terms';
  
  // Create new canonical tag with the correct URL for this route
  const canonical = document.createElement("link");
  canonical.setAttribute("rel", "canonical");
  canonical.setAttribute("href", `${BASE_URL}${canonicalPath}`);
  document.head.appendChild(canonical);
}

export default function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = resolveTitle(pathname);
    setMetaDescription(DESCRIPTIONS[pathname] || DEFAULT_DESC);
    setCanonicalUrl(pathname);
  }, [pathname]);
  return null;
}
